import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SCHEME_LIST } from "@/lib/schemes";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tractor, Droplets, ArrowRight, ShieldCheck, FileText, CheckCircle2, Send,
  UserCheck, Calculator, MapPin, Phone, Mail, Leaf, Sprout,
} from "lucide-react";

const stats = [
  { num: "98%", label: "Documentation Accuracy" },
  { num: "28", label: "States & UTs Served" },
  { num: "Up to 50%", label: "Tractor Subsidy" },
];

const steps = [
  { n: "01", icon: UserCheck, color: "#2e8b52", title: "Register & Login", desc: "Create your secure farmer account with email or Google." },
  { n: "02", icon: FileText, color: "#2563eb", title: "Fill Application", desc: "Enter your details and upload required documents." },
  { n: "03", icon: CheckCircle2, color: "#c9821f", title: "Verification", desc: "Our team reviews your application and documents." },
  { n: "04", icon: Send, color: "#7c3aed", title: "Submission", desc: "Clean application forwarded to the department." },
  { n: "05", icon: ShieldCheck, color: "#1f6b3b", title: "Track Status", desc: "Follow progress and get real-time updates." },
];

export default function Home() {
  const navigate = useNavigate();
  const [acres, setAcres] = useState("2");
  const [cost, setCost] = useState("650000");

  const subsidyPct = 45;
  const est = Math.round((Number(cost || 0) * subsidyPct) / 100);
  const eligible = Number(acres || 0) >= 1.5;

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="hero-radial relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-sm font-semibold text-emerald-800">
              <Leaf className="h-4 w-4" /> Empowering Farmers Across India
            </span>
            <h1 className="mt-5 font-head font-extrabold tracking-tight text-emerald-950 text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
              Apply for Farm Subsidies,{" "}
              <span className="brand-green">Without the Paperwork Stress</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              We help farmers and rural entrepreneurs prepare and submit applications for central & state benefit schemes — PMEGP, NLM, SMAM, VB-G RAM-G and Namo Drone Didi — with simple guidance and document help at every step.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-full bg-brand hover:bg-emerald-800 text-white px-7 h-12 text-base shadow-lg shadow-emerald-900/15" onClick={() => navigate("/login")} data-testid="hero-apply-now-button">
                Apply as Farmer <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full border-emerald-300 text-emerald-800 h-12 px-7 text-base hover:bg-emerald-50" onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}>
                View Services
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-900">{s.num}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-[28px] overflow-hidden shadow-2xl shadow-emerald-900/20 ring-1 ring-black/5">
              <img src="/hero.jpg" alt="Farmer ploughing field with a tractor" className="w-full h-[340px] sm:h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 ring-1 ring-emerald-100">
              <div className="h-11 w-11 rounded-xl bg-emerald-50 grid place-items-center">
                <ShieldCheck className="h-6 w-6 text-emerald-700" />
              </div>
              <div>
                <div className="font-head font-bold text-emerald-950 text-sm">Trusted & Transparent</div>
                <div className="text-xs text-slate-500">Consent-based application support</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-emerald-100 bg-white/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-x-8 gap-y-2 text-sm font-medium text-emerald-800">
            {["Transparent Process", "Document Guidance", "Dedicated Support", "Consent-Based Data Handling"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Our Services</span>
            <h2 className="mt-3 font-head font-bold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight">Government Schemes We Help With</h2>
            <p className="mt-4 text-slate-600">We assist with application preparation and submission for these central & state benefit schemes. Click a scheme to learn more and apply.</p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SCHEME_LIST.map((s) => (
              <button key={s.code} onClick={() => navigate(`/scheme/${s.code}`)} data-testid={`service-scheme-${s.code}`} className="group text-left bg-white rounded-3xl p-7 border border-emerald-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="h-14 w-14 rounded-2xl grid place-items-center" style={{ backgroundColor: s.tint }}>
                  <s.icon className="h-7 w-7" style={{ color: s.color }} />
                </div>
                <div className="mt-4 text-xs font-mono font-semibold text-slate-400">{s.code}</div>
                <h3 className="mt-1 font-head font-bold text-lg text-emerald-950 leading-snug">{s.name}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.short}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm group-hover:gap-2.5 transition-all">View & Apply <ArrowRight className="h-4 w-4" /></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-24 bg-[#f1f6f2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Process</span>
            <h2 className="mt-3 font-head font-bold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight">How It Works</h2>
            <p className="mt-4 text-slate-600">Five simple steps to prepare and submit your subsidy application.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {steps.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-xs font-mono font-semibold text-slate-400">STEP {s.n}</div>
                <div className="mt-3 h-11 w-11 rounded-xl grid place-items-center" style={{ backgroundColor: s.color }}>
                  <s.icon className="h-5 w-5 text-white" />
                </div>
                <h4 className="mt-4 font-head font-semibold text-emerald-950">{s.title}</h4>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility calculator */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-700"><Calculator className="h-4 w-4" /> Quick Estimate</span>
            <h2 className="mt-3 font-head font-bold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight">Subsidy Eligibility Calculator</h2>
            <p className="mt-4 text-slate-600 max-w-md">Get an indicative estimate of your possible tractor subsidy. Final eligibility and percentage are decided by the concerned government department.</p>
            <div className="mt-6 flex items-center gap-2 text-sm text-emerald-800 font-medium"><Sprout className="h-4 w-4" /> Typical tractor subsidy: 25% – 50% of cost</div>
          </div>
          <Card className="rounded-3xl p-7 border-emerald-100 shadow-lg shadow-emerald-900/5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600">Land holding (acres)</Label>
                <Input type="number" value={acres} onChange={(e) => setAcres(e.target.value)} className="mt-1.5 rounded-xl" data-testid="calc-acres-input" />
              </div>
              <div>
                <Label className="text-slate-600">Tractor cost (₹)</Label>
                <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="mt-1.5 rounded-xl" data-testid="calc-cost-input" />
              </div>
            </div>
            <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Estimated subsidy ({subsidyPct}%)</span>
                <span className="font-head font-extrabold text-2xl text-emerald-800" data-testid="calc-result">₹ {est.toLocaleString("en-IN")}</span>
              </div>
              <div className={`mt-3 text-sm font-medium ${eligible ? "text-emerald-700" : "text-amber-700"}`}>
                {eligible ? "✓ Land holding meets the common minimum for most schemes." : "⚠ Some schemes need a minimum land holding (often ~1.5 acres)."}
              </div>
            </div>
            <Button className="mt-5 w-full rounded-full bg-brand hover:bg-emerald-800 text-white h-11" onClick={() => navigate("/login")} data-testid="calc-apply-button">
              Start My Application <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Card>
        </div>
      </section>

      {/* Apply band */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[32px] bg-emerald-900 px-8 py-14 sm:px-14 text-center grain">
            <h2 className="font-head font-extrabold text-white text-2xl sm:text-3xl lg:text-4xl">Ready to Apply?</h2>
            <p className="mt-4 text-emerald-100/80 max-w-xl mx-auto">Start your subsidy application today. You'll be asked to log in with your email or Google account first to keep your information secure.</p>
            <Button size="lg" className="mt-8 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 h-12 px-8 text-base font-semibold" onClick={() => navigate("/login")} data-testid="band-apply-button">
              Apply Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Get In Touch</span>
            <h2 className="mt-3 font-head font-bold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight">Contact Us</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <ContactCard icon={MapPin} tint="#eaf5ee" color="#1f6b3b" title="Office Address" lines={["No: 50/33, Chinapanhalli,", "Marathalli, Bengaluru,", "Karnataka - 560037"]} />
            <ContactCard icon={Phone} tint="#e6f0fb" color="#2563eb" title="Phone" lines={["+91 87627 65882"]} action={{ href: "tel:+918762765882", label: "Tap to Call" }} />
            <ContactCard icon={Mail} tint="#fdf3e0" color="#c9821f" title="Email" lines={["support@novaagrisolution.com"]} action={{ href: "mailto:support@novaagrisolution.com", label: "Tap to Email" }} />
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

const ServiceCard = ({ icon: Icon, title, desc, tint, iconColor, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="group text-left bg-white rounded-3xl p-7 border border-emerald-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
    <div className="h-14 w-14 rounded-2xl grid place-items-center" style={{ backgroundColor: tint }}>
      <Icon className="h-7 w-7" style={{ color: iconColor }} />
    </div>
    <h3 className="mt-5 font-head font-bold text-xl text-emerald-950">{title}</h3>
    <p className="mt-2.5 text-slate-500 leading-relaxed text-[15px]">{desc}</p>
    <span className="mt-4 inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm group-hover:gap-2.5 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
  </button>
);

const ContactCard = ({ icon: Icon, title, lines, tint, color, action }) => (
  <Card className="rounded-3xl p-7 border-emerald-100 text-center">
    <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center" style={{ backgroundColor: tint }}>
      <Icon className="h-6 w-6" style={{ color }} />
    </div>
    <h4 className="mt-4 font-head font-semibold text-emerald-950">{title}</h4>
    <div className="mt-2 text-sm text-slate-500 leading-relaxed">{lines.map((l) => <div key={l}>{l}</div>)}</div>
    {action && <a href={action.href} className="mt-3 inline-block text-sm font-semibold text-emerald-700 hover:underline">{action.label}</a>}
  </Card>
);
